/**
 * Fire on Teslyn Cloud's staff maps (decision 806; docs/specs/cloud/fire-feeds.md §5): what `cloud_fires` answers,
 * read and drawn with the rules Teslyn Field draws by (`fire-incidents.ts`, `hotspots.ts`), so the Operations Hub, a
 * project's map and Teslyn Field show the same fires in the same colours with the same words, credits and notices.
 * Staff only: the client portal never asks.
 */
import {
  FIRE_FEED_IDS,
  fireFeatures,
  fireIncidentsOf,
  type FireFeedCopy,
  type FireFeedId,
  type FireLayer,
} from './fire-incidents.js';
import { hotspotFeatures, readHotspotCopy, type HotspotCell, type HotspotLayer } from './hotspots.js';
import type { LegendEntry, MapFeature } from './map-layers.js';

/** `cloud_fires(tenant)` as read. */
export interface CloudFiresAnswer {
  /** The organisation's fire_feeds switch (decision 805): the state fires are offered only while it is on. */
  readonly fireFeeds: boolean;
  readonly feeds: readonly FireFeedCopy[];
  /** Every square of the organisation's boxes (a square two boxes share once, its newest). */
  readonly cells: readonly HotspotCell[];
  /** When DEA last answered for any of the boxes (ISO), or null. */
  readonly hotspotsFetchedAt: string | null;
  /** A box held as many hot spots as were asked for: there may be more. */
  readonly hotspotsFull: boolean;
}

const isObj = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

/** The function's answer, or null when it is not one. */
export function readCloudFires(raw: unknown): CloudFiresAnswer | null {
  if (!isObj(raw) || !Array.isArray(raw.feeds) || !Array.isArray(raw.hotspots)) return null;
  const feeds: FireFeedCopy[] = [];
  for (const f of raw.feeds) {
    if (!isObj(f) || typeof f.body !== 'string' || typeof f.fetched_at !== 'string') continue;
    if (!(FIRE_FEED_IDS as readonly string[]).includes(String(f.feed))) continue;
    if (!Number.isFinite(Date.parse(f.fetched_at))) continue;
    feeds.push({ feed: f.feed as FireFeedId, body: f.body, fetchedAt: new Date(f.fetched_at).toISOString() });
  }
  const cells = new Map<string, HotspotCell>();
  let fetchedAt: string | null = null;
  let full = false;
  for (const b of raw.hotspots) {
    if (!isObj(b)) continue;
    const copy = readHotspotCopy({
      box: { south: b.south, west: b.west, north: b.north, east: b.east },
      fetchedAt: b.fetched_at,
      cells: b.cells,
      full: b.full,
    });
    if (!copy) continue;
    const at = new Date(copy.fetchedAt).toISOString();
    if (fetchedAt === null || at > fetchedAt) fetchedAt = at;
    full ||= copy.full;
    for (const c of copy.cells) {
      const had = cells.get(c.key);
      if (!had || c.newest.observedAt > had.newest.observedAt) cells.set(c.key, c);
    }
  }
  return {
    fireFeeds: raw.fire_feeds === true,
    feeds,
    cells: [...cells.values()],
    hotspotsFetchedAt: fetchedAt,
    hotspotsFull: full,
  };
}

export interface CloudFireLayers {
  /** The state fires, while shown and the switch is on. */
  readonly fires: FireLayer | null;
  readonly hotspots: HotspotLayer | null;
  /** Hot spots under fires, bottom first. */
  readonly features: MapFeature[];
  readonly legend: LegendEntry[];
  readonly credits: string[];
  /** Feeds whose copy is over 12 hours old (not drawn). */
  readonly tooOld: readonly FireFeedId[];
}

/** The fire layers of a staff map: planned burns without a warning are left out, as Teslyn Field's first look. */
export function cloudFireLayers(
  a: CloudFiresAnswer | null,
  nowMs: number,
  show: { readonly fires: boolean; readonly hotspots: boolean },
): CloudFireLayers {
  const firesOn = show.fires && a !== null && a.fireFeeds;
  const data = firesOn ? fireIncidentsOf(a.feeds, nowMs) : null;
  const fires = data ? fireFeatures(data.incidents, { plannedBurns: false }) : null;
  const hotspots = show.hotspots && a ? hotspotFeatures(a.cells, nowMs) : null;
  return {
    fires,
    hotspots,
    features: [...(hotspots?.features ?? []), ...(fires?.features ?? [])],
    legend: [...(fires?.legend ?? []), ...(hotspots?.legend ?? [])],
    credits: [...(fires?.credits ?? []), ...(hotspots?.credits ?? [])],
    tooOld: data?.tooOld ?? [],
  };
}

/** The pages a fire or hot spot links to in Teslyn Cloud (the agencies' and DEA's own, as Teslyn Field's allow-list). */
const FIRE_LINKS: readonly RegExp[] = [
  /^https:\/\/emergency\.wa\.gov\.au\/(incidents|warnings)\/[0-9a-f]{24}$/,
  /^https:\/\/www\.rfs\.nsw\.gov\.au\/fire-information\/fires-near-me$/,
  /^https:\/\/www\.fire\.qld\.gov\.au\/current-incidents$/,
  /^https:\/\/hotspots\.dea\.ga\.gov\.au\/$/,
];

/** Whether a feature's link may be shown as a link (anything else from a feed is not). */
export const fireLinkAllowed = (url: string): boolean => FIRE_LINKS.some((r) => r.test(url));

/** The agencies' sites, for the addresses inside their own words (Emergency WA's "make a plan" pages). */
const AGENCY_SITES =
  /^https:\/\/(emergency\.wa\.gov\.au|www\.rfs\.nsw\.gov\.au|www\.fire\.qld\.gov\.au)(\/[^\s]*)?$/;

/** Whether an address in a feed's words may be a link: a feature's own page, an agency's site, or a phone number. */
export const textLinkAllowed = (url: string): boolean =>
  fireLinkAllowed(url) || AGENCY_SITES.test(url) || /^tel:\+?\d{6,15}$/.test(url);

/** A piece of a feed's words: plain, or a link with the words to show for it. */
export interface TextPart {
  readonly text: string;
  readonly url?: string;
}

const IN_TEXT = /\s*\[(https?:\/\/[^\]\s]+|tel:[^\]\s]+)\]|(https?:\/\/[^\s\])]+)/g;

/**
 * A feed's words with their addresses picked out (R247): Emergency WA writes "Emergency WA
 * [https://emergency.wa.gov.au/plan]" and "1800 633 422 [tel:1800633422]". The brackets go; an address shows as its
 * site and path, a phone number as "call". A map says nothing about whether a link may be followed: `textLinkAllowed`.
 */
export function textWithLinks(value: string): TextPart[] {
  const out: TextPart[] = [];
  let from = 0;
  for (const m of value.matchAll(IN_TEXT)) {
    const raw = (m[1] ?? m[2])!;
    const url = raw.replace(/[.,;:]+$/, '');
    const start = m.index;
    if (start > from) out.push({ text: value.slice(from, start) });
    const spaced = m[1] !== undefined && m[0].startsWith('[') === false;
    out.push({
      text: `${spaced ? ' ' : ''}${url.startsWith('tel:') ? 'call' : url.replace(/^https?:\/\//, '')}`,
      url,
    });
    // punctuation after a bare address is the sentence's
    from = start + m[0].length - (raw.length - url.length);
  }
  if (from < value.length) out.push({ text: value.slice(from) });
  return out;
}
